/**
 * Shared custom-mode limit copy and parsing (MODE_LIMIT_REACHED contract).
 */

export const MODE_LIMIT_CODE = 'MODE_LIMIT_REACHED' as const;

export type ModeLimitContext = 'create' | 'activate';

export type ModeLimitSurface = 'app' | 'onboarding';

export type ModeLimitDetail = {
    code: typeof MODE_LIMIT_CODE;
    message: string;
    limit?: number;
    current?: number;
    upgrade_url?: string;
};

function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function isSameOriginHttpUrl(href: string): boolean {
    if (!/^https?:\/\//i.test(href)) {
        return false;
    }
    try {
        const u = new URL(href);
        if (typeof window === 'undefined' || !window.location?.origin) {
            return true;
        }
        return u.origin === window.location.origin;
    } catch {
        return false;
    }
}

export function resolveSafeModeLimitUpgradeUrl(
    upgradeUrl: string | undefined,
    fallback = '/app/billing',
): { href: string; useExternal: boolean } {
    const external = upgradeUrl?.trim();
    if (external && isSameOriginHttpUrl(external)) {
        return { href: external, useExternal: true };
    }
    return { href: fallback, useExternal: false };
}

export function parseModeLimitDetail(detail: unknown): ModeLimitDetail | null {
    if (!isRecord(detail)) return null;
    if (detail.code !== MODE_LIMIT_CODE) return null;
    const message = typeof detail.message === 'string' ? detail.message : '';
    if (!message.trim()) return null;
    return {
        code: MODE_LIMIT_CODE,
        message,
        limit: typeof detail.limit === 'number' ? detail.limit : undefined,
        current: typeof detail.current === 'number' ? detail.current : undefined,
        upgrade_url: typeof detail.upgrade_url === 'string' ? detail.upgrade_url : undefined,
    };
}

/** Axios/FastAPI error shape: response.data.detail or baseline error.details. */
export function parseModeLimitFromApiError(error: unknown): ModeLimitDetail | null {
    const anyErr = error as {
        response?: { data?: { detail?: unknown; error?: { details?: unknown } } };
    };
    const detail = anyErr?.response?.data?.detail;
    const fromDetail = parseModeLimitDetail(detail);
    if (fromDetail) return fromDetail;
    const nested = anyErr?.response?.data?.error?.details;
    return parseModeLimitDetail(nested);
}

export function modeLimitUsageLine(detail: ModeLimitDetail): string | null {
    if (detail.current != null && detail.limit != null) {
        return `${detail.current} of ${detail.limit} active custom modes on your plan`;
    }
    if (detail.limit != null) {
        return `Plan active custom-mode limit: ${detail.limit}`;
    }
    return null;
}

export function modeLimitBlockedTitle(context: ModeLimitContext): string {
    return context === 'create' ? 'Create a custom mode' : 'Activate this mode';
}

export function modeLimitWhyLine(context: ModeLimitContext): string {
    return context === 'create'
        ? 'Your active custom-mode limit has been reached.'
        : 'Activating this mode would exceed your active custom-mode limit.';
}

export function modeLimitContextHint(context: ModeLimitContext): string | null {
    if (context === 'activate') {
        return 'Archived and deactivated modes do not count toward your active limit.';
    }
    return null;
}

export function modeLimitRemediationLine(surface: ModeLimitSurface): string {
    if (surface === 'onboarding') {
        return 'Select a built-in mode above to continue onboarding. You can start a Pro trial in the next steps, or manage custom modes after setup.';
    }
    return 'Archive or deactivate another active custom mode, or upgrade your plan.';
}

export function proactiveModeLimitMessage(limit: number, current?: number): string {
    const usage =
        typeof current === 'number'
            ? `You're using ${current} of ${limit} custom modes allowed on your current plan.`
            : `Your current plan allows ${limit} active custom mode${limit === 1 ? '' : 's'}.`;
    return `${usage} Open Billing to manage your plan.`;
}
